import { apiClient } from './api'
import type {
  Product,
  ProductListItem,
  ProductStatus,
  ProductCategory,
  ProductVariant,
  ProductImage,
  CreateProductRequest,
  UpdateProductRequest,
  UpdateInventoryRequest,
  CreateProductVariantRequest,
  UpdateProductVariantRequest,
  AddProductImageRequest,
  ReorderImagesRequest,
  CreateProductCategoryRequest,
  UpdateProductCategoryRequest,
  CategoryOrderItem,
  ProductFilters,
  PaginatedResult,
  ProductImportRow,
  ProductImportResult,
  BulkCreateProductRequest,
  ProductImportDto,
  ProductImportListDto,
  CreateProductImportResponse,
  ProductExportDto,
  CreateProductExportResponse,
  ProductExportDownloadResponse,
  BulkEditProductsRequest,
  BulkEditProductsResponse,
  ProductOptionGroup
} from '@/types/shop'
import { ProductImportStatus, ProductExportStatus, UnitOfMeasure } from '@/types/shop'

export const shopProductService = {
  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Transform relative image URL to absolute URL
   * The API returns relative paths like /images/... but we need absolute URLs
   */
  _transformImageUrl(image: ProductImage): ProductImage {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:50000/api'
    // Remove /api suffix to get the base server URL
    const baseUrl = apiUrl.replace(/\/api\/?$/, '')
    return {
      ...image,
      imageUrl: image.imageUrl.startsWith('http') ? image.imageUrl : `${baseUrl}${image.imageUrl}`
    }
  },

  /**
   * Transform all images in a product
   */
  _transformProductImages(product: Product): Product {
    if (product.images && product.images.length > 0) {
      product.images = product.images.map(img => this._transformImageUrl(img))
    }
    return product
  },

  /**
   * Transform primary image URL in a product list item
   */
  _transformProductListItem(item: ProductListItem): ProductListItem {
    if (item.primaryImageUrl && !item.primaryImageUrl.startsWith('http')) {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:50000/api'
      const baseUrl = apiUrl.replace(/\/api\/?$/, '')
      return {
        ...item,
        primaryImageUrl: `${baseUrl}${item.primaryImageUrl}`
      }
    }
    return item
  },

  // ============================================================================
  // Products - Provider Management
  // ============================================================================

  /**
   * Get products for a shop with filtering and pagination
   */
  async getProducts(
    shopId: string,
    filters: ProductFilters = {}
  ): Promise<PaginatedResult<ProductListItem>> {
    const params = {
      search: filters.search,
      categoryId: filters.categoryId,
      // Roll-up: matches everything under this base category regardless of
      // sub-cat depth. AND-combines with categoryId when both are supplied.
      baseCategoryId: filters.baseCategoryId,
      status: filters.status,
      isFeatured: filters.isFeatured,
      inStock: filters.inStock,
      lowStock: filters.lowStock,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 20,
      sortBy: filters.sortBy ?? 'SortOrder',
      sortDirection: filters.sortDirection ?? 'asc'
    }
    const result = await apiClient.get<PaginatedResult<ProductListItem>>(
      `/shop/shops/${shopId}/products`,
      { params }
    )
    // Transform image URLs in the result
    result.items = result.items.map(item => this._transformProductListItem(item))
    return result
  },

  /**
   * Get product by ID
   */
  async getProduct(id: string): Promise<Product> {
    const product = await apiClient.get<Product>(`/shop/products/${id}`)
    return this._transformProductImages(product)
  },

  /**
   * Polish the product's title and description with AI. Capped at 3 attempts
   * per product (lifetime). Returns the generated copy plus remaining attempts.
   * Throws on 429 when the cap is reached — callers can inspect the error
   * response to show a friendly "no attempts left" message.
   */
  async polishProductWithAi(id: string): Promise<{ title: string; description: string; remainingAttempts: number }> {
    return await apiClient.post<{ title: string; description: string; remainingAttempts: number }>(
      `/shop/products/${id}/ai-polish`, {}
    )
  },

  /**
   * Sanitize product data - convert empty strings to null for optional fields
   */
  _sanitizeProductData<T>(data: T): T {
    const sanitized = { ...data } as Record<string, unknown>
    // Fields that should be null instead of empty string
    const nullableFields = ['sku', 'barcode', 'description', 'shortDescription', 'metaTitle', 'metaDescription', 'weightUnit']
    for (const field of nullableFields) {
      if (field in sanitized && sanitized[field] === '') {
        sanitized[field] = null
      }
    }
    return sanitized as T
  },

  /**
   * Create a new product
   */
  async createProduct(shopId: string, data: CreateProductRequest): Promise<Product> {
    const sanitizedData = this._sanitizeProductData(data)
    const product = await apiClient.post<Product>(`/shop/shops/${shopId}/products`, sanitizedData)
    return this._transformProductImages(product)
  },

  /**
   * Update product
   */
  async updateProduct(id: string, data: UpdateProductRequest): Promise<Product> {
    const sanitizedData = this._sanitizeProductData(data)
    const product = await apiClient.put<Product>(`/shop/products/${id}`, sanitizedData)
    return this._transformProductImages(product)
  },

  /**
   * Archive product (soft delete)
   */
  async archiveProduct(id: string): Promise<void> {
    await apiClient.delete(`/shop/products/${id}`)
  },

  async hardDeleteProducts(productIds: string[], twoFactorCode: string): Promise<{ deleted: number; failed: number; results: any[] }> {
    return await apiClient.post('/shop/products/hard-delete', { productIds, twoFactorCode })
  },

  /**
   * Update product inventory
   */
  async updateInventory(id: string, data: UpdateInventoryRequest): Promise<Product> {
    const product = await apiClient.put<Product>(`/shop/products/${id}/inventory`, data)
    return this._transformProductImages(product)
  },

  /**
   * Get low stock products
   */
  async getLowStockProducts(shopId: string): Promise<ProductListItem[]> {
    const items = await apiClient.get<ProductListItem[]>(`/shop/shops/${shopId}/products/low-stock`)
    return items.map(item => this._transformProductListItem(item))
  },

  /**
   * Get out of stock products
   */
  async getOutOfStockProducts(shopId: string): Promise<ProductListItem[]> {
    const items = await apiClient.get<ProductListItem[]>(`/shop/shops/${shopId}/products/out-of-stock`)
    return items.map(item => this._transformProductListItem(item))
  },

  /**
   * Bulk update status for multiple products
   */
  async bulkUpdateStatus(shopId: string, productIds: string[], status: ProductStatus): Promise<{ updatedCount: number; skippedCount: number }> {
    return await apiClient.put<{ updatedCount: number; skippedCount: number }>(
      `/shop/shops/${shopId}/products/bulk-status`,
      { productIds, status }
    )
  },

  /**
   * Bulk edit multiple products (price, stock, category, featured)
   */
  async bulkEditProducts(shopId: string, request: BulkEditProductsRequest): Promise<BulkEditProductsResponse> {
    return await apiClient.put<BulkEditProductsResponse>(
      `/shop/shops/${shopId}/products/bulk-edit`,
      request
    )
  },

  // ============================================================================
  // Product Variants
  // ============================================================================

  /**
   * Add variant to product
   */
  async addVariant(productId: string, data: CreateProductVariantRequest): Promise<ProductVariant> {
    return await apiClient.post<ProductVariant>(`/shop/products/${productId}/variants`, data)
  },

  /**
   * Update variant
   */
  async updateVariant(productId: string, variantId: string, data: UpdateProductVariantRequest): Promise<ProductVariant> {
    return await apiClient.put<ProductVariant>(`/shop/products/${productId}/variants/${variantId}`, data)
  },

  /**
   * Delete variant
   */
  async deleteVariant(productId: string, variantId: string): Promise<void> {
    await apiClient.delete(`/shop/products/${productId}/variants/${variantId}`)
  },

  /**
   * Update variant inventory
   */
  async updateVariantInventory(productId: string, variantId: string, data: UpdateInventoryRequest): Promise<ProductVariant> {
    return await apiClient.put<ProductVariant>(
      `/shop/products/${productId}/variants/${variantId}/inventory`,
      data
    )
  },

  // ============================================================================
  // Product Images
  // ============================================================================

  /**
   * Add image to product by URL
   */
  async addImage(productId: string, data: AddProductImageRequest): Promise<ProductImage> {
    const image = await apiClient.post<ProductImage>(`/shop/products/${productId}/images`, data)
    return this._transformImageUrl(image)
  },

  /**
   * Upload image file to product
   */
  async uploadImage(productId: string, file: File, altText?: string): Promise<ProductImage> {
    const formData = new FormData()
    formData.append('file', file)
    if (altText) {
      formData.append('altText', altText)
    }

    const image = await apiClient.post<ProductImage>(
      `/shop/products/${productId}/images/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
    return this._transformImageUrl(image)
  },

  /**
   * Remove image from product
   */
  async removeImage(productId: string, imageId: string): Promise<void> {
    await apiClient.delete(`/shop/images/${imageId}`)
  },

  /**
   * Reorder product images
   */
  async reorderImages(productId: string, data: ReorderImagesRequest): Promise<void> {
    await apiClient.put(`/shop/products/${productId}/images/reorder`, data)
  },

  /**
   * Set an image as the primary image for its product
   */
  async setPrimaryImage(imageId: string): Promise<ProductImage> {
    const image = await apiClient.put<ProductImage>(`/shop/images/${imageId}/set-primary`, {})
    return this._transformImageUrl(image)
  },

  // ============================================================================
  // Categories - Provider Management
  // ============================================================================

  /**
   * Get categories for a shop as tree structure (includes inactive for management)
   */
  async getCategories(shopId: string): Promise<ProductCategory[]> {
    return await apiClient.get<ProductCategory[]>(`/shop/shops/${shopId}/categories?tree=true`)
  },

  /**
   * Get categories as flat list with full path (for dropdown selection)
   * Returns all categories with fullPath computed (e.g., "Electronics > Phones > Accessories")
   */
  async getCategoriesFlat(shopId: string): Promise<ProductCategory[]> {
    return await apiClient.get<ProductCategory[]>(`/shop/shops/${shopId}/categories`)
  },

  /**
   * Get category by ID
   */
  async getCategory(id: string): Promise<ProductCategory> {
    return await apiClient.get<ProductCategory>(`/shop/categories/${id}`)
  },

  /**
   * Create a new category
   */
  async createCategory(shopId: string, data: CreateProductCategoryRequest): Promise<ProductCategory> {
    return await apiClient.post<ProductCategory>(`/shop/shops/${shopId}/categories`, data)
  },

  /**
   * Update category
   */
  async updateCategory(id: string, data: UpdateProductCategoryRequest): Promise<ProductCategory> {
    return await apiClient.put<ProductCategory>(`/shop/categories/${id}`, data)
  },

  /**
   * Delete category
   */
  async deleteCategory(id: string): Promise<void> {
    await apiClient.delete(`/shop/categories/${id}`)
  },

  /**
   * Reorder categories
   */
  async reorderCategories(shopId: string, orderItems: CategoryOrderItem[]): Promise<void> {
    await apiClient.put(`/shop/shops/${shopId}/categories/reorder`, { orderItems })
  },

  // ============================================================================
  // Public Shop API
  // ============================================================================

  /**
   * Browse products in a public shop
   */
  async browseProducts(
    shopCode: string,
    filters: ProductFilters = {}
  ): Promise<PaginatedResult<ProductListItem>> {
    const params = {
      search: filters.search,
      categoryId: filters.categoryId,
      inStock: filters.inStock,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      page: filters.page ?? 1,
      pageSize: filters.pageSize ?? 20,
      sortBy: filters.sortBy ?? 'SortOrder',
      sortDirection: filters.sortDirection ?? 'asc'
    }
    return await apiClient.get<PaginatedResult<ProductListItem>>(
      `/shops/${shopCode}/products`,
      { params }
    )
  },

  /**
   * Get featured products for a public shop
   */
  async getFeaturedProducts(shopCode: string, count: number = 10): Promise<ProductListItem[]> {
    return await apiClient.get<ProductListItem[]>(
      `/shops/${shopCode}/products/featured`,
      { params: { count } }
    )
  },

  /**
   * Get product by slug for public shop
   */
  async getProductBySlug(shopCode: string, slug: string): Promise<Product> {
    return await apiClient.get<Product>(`/shops/${shopCode}/products/${slug}`)
  },

  /**
   * Get related products
   */
  async getRelatedProducts(shopCode: string, slug: string, count: number = 4): Promise<ProductListItem[]> {
    return await apiClient.get<ProductListItem[]>(
      `/shops/${shopCode}/products/${slug}/related`,
      { params: { count } }
    )
  },

  /**
   * Get public categories for a shop
   */
  async getPublicCategories(shopCode: string): Promise<ProductCategory[]> {
    return await apiClient.get<ProductCategory[]>(`/shops/${shopCode}/categories`)
  },

  /**
   * Get products by category for public shop
   */
  async getProductsByCategory(
    shopCode: string,
    categoryId: string,
    page: number = 1,
    pageSize: number = 20,
    sortBy: string = 'SortOrder',
    sortDirection: 'asc' | 'desc' = 'asc'
  ): Promise<PaginatedResult<ProductListItem>> {
    return await apiClient.get<PaginatedResult<ProductListItem>>(
      `/shops/${shopCode}/categories/${categoryId}/products`,
      { params: { page, pageSize, sortBy, sortDirection } }
    )
  },

  // ============================================================================
  // Bulk Import
  // ============================================================================

  /**
   * Parse CSV file and return preview data
   */
  parseCSVFile(file: File): Promise<ProductImportRow[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string
          const rows = this.parseCSVText(text)
          resolve(rows)
        } catch (error) {
          reject(error)
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })
  },

  /**
   * Parse CSV text into import rows
   */
  parseCSVText(text: string): ProductImportRow[] {
    // Filter out empty lines and comment lines (starting with #)
    const lines = text.split('\n').filter(line => {
      const trimmed = line.trim()
      return trimmed && !trimmed.startsWith('#')
    })

    if (lines.length < 2) {
      throw new Error('CSV file must have a header row and at least one data row')
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
    const requiredHeaders = ['name', 'price']
    const missingHeaders = requiredHeaders.filter(h => !headers.includes(h))
    if (missingHeaders.length > 0) {
      throw new Error(`Missing required columns: ${missingHeaders.join(', ')}`)
    }

    const rows: ProductImportRow[] = []

    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i])
      const row = this.mapRowToImportData(headers, values, i + 1)
      rows.push(row)
    }

    return rows
  },

  /**
   * Parse a CSV line handling quoted values
   */
  parseCSVLine(line: string): string[] {
    const values: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        values.push(current.trim())
        current = ''
      } else {
        current += char
      }
    }
    values.push(current.trim())
    return values
  },

  /**
   * Map CSV row values to import data structure
   */
  mapRowToImportData(headers: string[], values: string[], rowNumber: number): ProductImportRow {
    const row: ProductImportRow = {
      rowNumber,
      name: '',
      price: 0,
      status: 'valid',
      errors: [],
      warnings: []
    }

    headers.forEach((header, index) => {
      const value = values[index] || ''
      switch (header) {
        case 'name':
          row.name = value
          if (!value) row.errors.push('Name is required')
          break
        case 'sku':
          row.sku = value || undefined
          break
        case 'barcode':
          row.barcode = value || undefined
          break
        case 'category':
        case 'categoryname':
          row.categoryName = value || undefined
          break
        case 'category_level1':
        case 'categorylevel1':
          row.categoryLevel1 = value || undefined
          if (!value) row.errors.push('Category Level 1 (parent category) is required')
          break
        case 'category_level2':
        case 'categorylevel2':
          row.categoryLevel2 = value || undefined
          break
        case 'category_level3':
        case 'categorylevel3':
          row.categoryLevel3 = value || undefined
          break
        case 'price':
          const price = parseFloat(value)
          if (isNaN(price) || price < 0) {
            row.errors.push('Price must be a valid positive number')
            row.price = 0
          } else {
            row.price = price
          }
          break
        case 'compareatprice':
        case 'compare_at_price':
          if (value) {
            const comparePrice = parseFloat(value)
            if (!isNaN(comparePrice)) row.compareAtPrice = comparePrice
          }
          break
        case 'costprice':
        case 'cost_price':
          if (value) {
            const costPrice = parseFloat(value)
            if (!isNaN(costPrice)) row.costPrice = costPrice
          }
          break
        case 'description':
          row.description = value || undefined
          if (!value) row.errors.push('Description is required')
          break
        case 'shortdescription':
        case 'short_description':
          row.shortDescription = value || undefined
          if (!value) row.errors.push('Short Description is required')
          break
        case 'stockquantity':
        case 'stock':
        case 'stock_quantity':
          if (value) {
            const stock = parseInt(value, 10)
            if (!isNaN(stock)) row.stockQuantity = stock
          }
          break
        case 'lowstockthreshold':
        case 'low_stock_threshold':
          if (value) {
            const threshold = parseInt(value, 10)
            if (!isNaN(threshold)) row.lowStockThreshold = threshold
          }
          break
        case 'trackinventory':
        case 'track_inventory':
          row.trackInventory = value.toLowerCase() === 'true' || value === '1'
          break
        case 'isfeatured':
        case 'is_featured':
        case 'featured':
          row.isFeatured = value.toLowerCase() === 'true' || value === '1'
          break
        case 'unitofmeasure':
        case 'unit_of_measure':
        case 'uom':
          if (value) {
            const uom = this.parseUnitOfMeasure(value)
            if (uom) {
              row.unitOfMeasure = uom
            } else {
              row.errors.push(`Invalid unit of measure: ${value}. Valid values: Each, Kilogram, Gram, Litre, Millilitre, Metre, Centimetre, Pack, Box, Dozen`)
            }
          } else {
            row.errors.push('Unit of Measure is required')
          }
          break
        case 'weight':
          if (value) {
            const weight = parseFloat(value)
            if (!isNaN(weight)) row.weight = weight
          }
          break
        case 'weightunit':
        case 'weight_unit':
          row.weightUnit = value || undefined
          break
        case 'imageurl':
        case 'image_url':
        case 'image':
          if (value) {
            row.imageUrls = [value]
          }
          break
        case 'imageurls':
        case 'image_urls':
        case 'images':
          if (value) {
            row.imageUrls = value.split('|').map(url => url.trim()).filter(Boolean)
          }
          break
        case 'varianttitle':
        case 'variant_title':
        case 'variant':
          row.variantTitle = value || undefined
          break
        case 'payableonarrival':
        case 'payable_on_arrival':
          row.payableOnArrival = value.toLowerCase() === 'true' || value === '1'
          break
        case 'option_name':
        case 'optionname':
          row.optionName = value || undefined
          break
        case 'option_choices':
        case 'optionchoices':
          row.optionChoices = value || undefined
          break
        case 'option_prices':
        case 'optionprices':
          row.optionPrices = value || undefined
          break
      }
    })

    // Compute categoryName from 3 levels if not already set directly
    if (!row.categoryName && (row.categoryLevel1 || row.categoryLevel2 || row.categoryLevel3)) {
      const levels = [row.categoryLevel1, row.categoryLevel2, row.categoryLevel3].filter(Boolean)
      row.categoryName = levels.join(' > ')
    }

    // If this is a variant row (has variantTitle), clear errors for fields inherited from parent
    if (row.variantTitle) {
      // Variants inherit: category, description, shortDescription, unitOfMeasure from parent
      // Only require: name (to find parent), variantTitle, price
      row.errors = row.errors.filter(err =>
        !err.includes('Category Level 1') &&
        !err.includes('Description is required') &&
        !err.includes('Short Description is required') &&
        !err.includes('Unit of Measure is required')
      )
    }

    // Set row status
    if (row.errors.length > 0) {
      row.status = 'error'
    } else if (row.warnings.length > 0) {
      row.status = 'warning'
    }

    return row
  },

  /**
   * Parse unit of measure string
   */
  parseUnitOfMeasure(value: string): UnitOfMeasure | undefined {
    const normalized = value.toLowerCase().trim()
    const mapping: Record<string, UnitOfMeasure> = {
      'each': UnitOfMeasure.Each,
      'kilogram': UnitOfMeasure.Kilogram,
      'kg': UnitOfMeasure.Kilogram,
      'gram': UnitOfMeasure.Gram,
      'g': UnitOfMeasure.Gram,
      'litre': UnitOfMeasure.Litre,
      'liter': UnitOfMeasure.Litre,
      'l': UnitOfMeasure.Litre,
      'millilitre': UnitOfMeasure.Millilitre,
      'milliliter': UnitOfMeasure.Millilitre,
      'ml': UnitOfMeasure.Millilitre,
      'metre': UnitOfMeasure.Metre,
      'meter': UnitOfMeasure.Metre,
      'm': UnitOfMeasure.Metre,
      'centimetre': UnitOfMeasure.Centimetre,
      'centimeter': UnitOfMeasure.Centimetre,
      'cm': UnitOfMeasure.Centimetre,
      'pack': UnitOfMeasure.Pack,
      'box': UnitOfMeasure.Box,
      'dozen': UnitOfMeasure.Dozen
    }
    return mapping[normalized]
  },

  /**
   * Bulk create products from import data
   */
  async bulkCreateProducts(
    shopId: string,
    data: BulkCreateProductRequest
  ): Promise<ProductImportResult> {
    return await apiClient.post<ProductImportResult>(
      `/shop/shops/${shopId}/products/bulk`,
      data
    )
  },

  /**
   * Generate CSV template for product import
   * Clean template with 3 category level columns, no instructions (shown on UI instead)
   * Supports importing products and their variants
   * @param categories - Optional array of category info to include as examples
   */
  generateCSVTemplate(categories?: { name: string; depth: number; parentName?: string; grandparentName?: string }[], baseCategories?: string[]): string {
    // Headers - Required fields are: name, price
    // For products: title, category_level1, description, shortDescription, unitOfMeasure required
    // For variants: variantTitle required (others inherited from parent product)
    const headers = [
      'name',
      'title',
      'variantTitle',     // If present, this row is a variant of the product with matching 'name'
      'sku',
      'barcode',
      'base_category',
      'category_level1',
      'category_level2',
      'category_level3',
      'price',
      'compareAtPrice',
      'costPrice',
      'description',
      'shortDescription',
      'stockQuantity',
      'lowStockThreshold',
      'trackInventory',
      'maxQuantityPerOrder', // 1-10, max qty customer can order (defaults to 10)
      'isFeatured',
      'isExtra',
      'payableOnArrival', // true/false - customer pays on arrival instead of checkout
      'isVegan',          // true/false - suitable for vegans (no animal products)
      'isVegetarian',     // true/false - suitable for vegetarians (no meat/fish)
      'isHalal',          // true/false - halal certified/compliant (defaults to true)
      'unitOfMeasure',
      'weight',
      'weightUnit',
      'imageUrls',
      'option_name',     // Option group name (e.g., Cut Type, Size)
      'option_choices',  // Comma-separated choices (max 5, e.g., Curry Cut, Nihari Cut, Biryani Cut)
      'option_prices'    // Comma-separated prices matching choices (e.g., 0, 0, 1.00) - 0 = no extra charge
    ]

    const rows: string[] = []

    // Add header row
    rows.push(headers.join(','))

    // Helper to escape CSV values
    const escapeCSV = (value: string): string => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    }

    // If base categories provided, create example rows using them
    if (baseCategories && baseCategories.length > 0) {
      baseCategories.slice(0, 5).forEach((baseCatName, index) => {
        const baseCategory = baseCatName
        const level1 = '' // User fills in sub-categories
        const level2 = ''

        // Product row (no variantTitle)
        const exampleRow = [
          escapeCSV(`Example Product ${index + 1}`),  // name (required)
          escapeCSV(`Amazing ${baseCatName} Product - Best Quality`), // title (required for products)
          '',                                          // variantTitle (empty = this is a product)
          `SKU-${String(index + 1).padStart(3, '0')}`, // sku
          '',                                          // barcode
          escapeCSV(baseCategory),                    // base_category (required - e.g. Meat, Grocery)
          escapeCSV(level1),                          // category_level1 (e.g. Chicken, Lamb)
          escapeCSV(level2),                          // category_level2 (e.g. Breast, Thigh)
          '',                                          // category_level3 (e.g. Boneless, Skin-On)
          ((19.99 + index * 10).toFixed(2)),          // price (required)
          '',                                          // compareAtPrice
          '',                                          // costPrice
          escapeCSV(`Full description for ${baseCatName} product. This is a detailed description.`), // description (required for products)
          escapeCSV(`Short desc for ${baseCatName}`),    // shortDescription (required for products)
          '100',                                       // stockQuantity
          '10',                                        // lowStockThreshold
          'true',                                      // trackInventory
          '10',                                        // maxQuantityPerOrder
          'false',                                     // isFeatured
          'false',                                     // isExtra (add-on product)
          'false',                                     // payableOnArrival
          'false',                                     // isVegan
          'false',                                     // isVegetarian
          'true',                                      // isHalal (defaults to true)
          'Each',                                      // unitOfMeasure (required for products)
          '',                                          // weight
          '',                                          // weightUnit
          '',                                          // imageUrls
          index === 0 ? 'Cut Type' : '',               // option_name
          index === 0 ? escapeCSV('Curry Cut, Nihari Cut, Biryani Cut, Whole') : '', // option_choices
          index === 0 ? escapeCSV('0, 0, 1.00, 0') : '' // option_prices
        ]
        rows.push(exampleRow.join(','))

        // Add variant example for first product only
        if (index === 0) {
          // Variant row - same name, with variantTitle
          const variantRow = [
            escapeCSV(`Example Product ${index + 1}`),  // name (same as parent product)
            '',                                          // title (inherited from parent)
            'Small',                                     // variantTitle (this makes it a variant)
            `SKU-${String(index + 1).padStart(3, '0')}-S`, // sku
            '',                                          // barcode
            '',                                          // base_category (inherited)
            '',                                          // category_level1 (inherited)
            '',                                          // category_level2 (inherited)
            '',                                          // category_level3 (inherited)
            ((19.99 + index * 10).toFixed(2)),          // price (required)
            '',                                          // compareAtPrice
            '',                                          // costPrice
            '',                                          // description (inherited)
            '',                                          // shortDescription (inherited)
            '50',                                        // stockQuantity
            '5',                                         // lowStockThreshold
            'true',                                      // trackInventory
            '',                                          // isFeatured (inherited)
            '',                                          // isExtra (inherited)
            'false',                                     // payableOnArrival
            '',                                          // isVegan (inherited)
            '',                                          // isVegetarian (inherited)
            '',                                          // isHalal (inherited)
            '',                                          // unitOfMeasure (inherited)
            '',                                          // weight
            '',                                          // weightUnit
            '',                                          // imageUrls
            '',                                          // option_name (inherited)
            '',                                          // option_choices (inherited)
            ''                                           // option_prices (inherited)
          ]
          rows.push(variantRow.join(','))
        }
      })
    } else {
      // No categories - provide generic examples showing products and variants
      // Headers: name, title, variantTitle, sku, barcode, category_level1, category_level2, category_level3, price, compareAtPrice, costPrice, description, shortDescription, stockQuantity, lowStockThreshold, trackInventory, isFeatured, isExtra, payableOnArrival, isVegan, isVegetarian, isHalal, unitOfMeasure, weight, weightUnit, imageUrls
      const examples = [
        ['Example Product 1', 'Amazing T-Shirt - Best Quality', '', 'SKU-001', '', 'Clothing', '', '', '29.99', '', '', 'Full description for this t-shirt.', 'Short desc 1', '100', '10', 'true', '10', 'false', 'false', 'false', 'true', 'true', 'true', 'Each', '', '', '', '', '', ''],
        ['Example Product 1', '', 'Small', 'SKU-001-S', '', '', '', '', '29.99', '', '', '', '', '50', '5', 'true', '', '', '', 'false', '', '', '', '', '', '', '', '', '', ''],
        ['Example Product 1', '', 'Medium', 'SKU-001-M', '', '', '', '', '29.99', '', '', '', '', '50', '5', 'true', '', '', '', 'false', '', '', '', '', '', '', '', '', '', ''],
        ['Example Product 1', '', 'Large', 'SKU-001-L', '', '', '', '', '31.99', '', '', '', '', '50', '5', 'true', '', '', '', 'false', '', '', '', '', '', '', '', '', '', ''],
        ['Hair Cut Service', 'Professional Hair Cut', '', 'SKU-002', '', 'Services', '', '', '45.00', '', '', 'Professional haircut service at our salon.', 'Haircut', '999', '10', 'false', '10', 'false', 'false', 'true', 'false', 'false', 'true', 'Each', '', '', '', '', '', ''],
        ['Hair Cut Service', '', 'Men\'s Cut', 'SKU-002-M', '', '', '', '', '35.00', '', '', '', '', '999', '10', 'false', '', '', '', 'true', '', '', '', '', '', '', '', '', '', ''],
        ['Hair Cut Service', '', 'Women\'s Cut', 'SKU-002-W', '', '', '', '', '55.00', '', '', '', '', '999', '10', 'false', '', '', '', 'true', '', '', '', '', '', '', '', '', '', ''],
        ['Gift Wrapping', 'Premium Gift Wrapping Service', '', 'EXTRA-001', '', 'Extras', '', '', '2.99', '', '', 'Premium gift wrapping service.', 'Gift wrap', '999', '10', 'false', '10', 'false', 'true', 'false', 'false', 'false', 'true', 'Each', '', '', '', '', '', '']
      ]
      examples.forEach(row => rows.push(row.join(',')))
    }

    // Return clean CSV - no instructions (shown on Import page UI instead)
    return rows.join('\n')
  },

  /**
   * Download CSV template
   * @param categories - Optional array of category info to include as examples
   */
  downloadCSVTemplate(categories?: { name: string; depth: number; parentName?: string; grandparentName?: string }[], baseCategories?: string[]): void {
    const template = this.generateCSVTemplate(categories, baseCategories)
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'product_import_template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  },

  // ============================================================================
  // Background Import API (Server-side processing)
  // ============================================================================

  /**
   * Upload CSV file for background import processing
   * Returns immediately with import job ID - processing happens in background
   */
  async createImport(shopId: string, file: File): Promise<CreateProductImportResponse> {
    const formData = new FormData()
    formData.append('file', file)

    return await apiClient.post<CreateProductImportResponse>(
      `/shop/shops/${shopId}/imports`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )
  },

  /**
   * Get import job details by ID
   */
  async getImport(importId: string): Promise<ProductImportDto> {
    return await apiClient.get<ProductImportDto>(`/shop/imports/${importId}`)
  },

  /**
   * List import history for a shop with pagination
   */
  async getImports(
    shopId: string,
    page = 1,
    pageSize = 20
  ): Promise<PaginatedResult<ProductImportListDto>> {
    return await apiClient.get<PaginatedResult<ProductImportListDto>>(
      `/shop/shops/${shopId}/imports`,
      { params: { page, pageSize } }
    )
  },

  /**
   * Download original uploaded CSV file.
   * Returns downloadUrl for S3, or triggers direct download for local storage.
   */
  async downloadOriginalFile(importId: string, defaultFileName: string = 'import.csv'): Promise<void> {
    const response = await apiClient.request({
      method: 'GET',
      url: `/shop/imports/${importId}/download/original`,
      responseType: 'blob'
    })

    // Check if it's a JSON response (pre-signed URL)
    const contentType = response.headers?.['content-type'] || ''
    if (contentType.includes('application/json')) {
      // Parse JSON to get download URL
      const text = await (response.data as Blob).text()
      const json = JSON.parse(text)
      if (json.downloadUrl) {
        window.open(json.downloadUrl, '_blank')
        return
      }
    }

    // It's a file blob - trigger download
    const blob = response.data as Blob
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // Try to get filename from content-disposition header
    const disposition = response.headers?.['content-disposition']
    let filename = defaultFileName
    if (disposition) {
      const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  /**
   * Download result CSV file (with import_status and import_error columns).
   * Returns downloadUrl for S3, or triggers direct download for local storage.
   */
  async downloadResultFile(importId: string, defaultFileName: string = 'import_result.csv'): Promise<void> {
    const response = await apiClient.request({
      method: 'GET',
      url: `/shop/imports/${importId}/download/result`,
      responseType: 'blob'
    })

    // Check if it's a JSON response (pre-signed URL)
    const contentType = response.headers?.['content-type'] || ''
    if (contentType.includes('application/json')) {
      // Parse JSON to get download URL
      const text = await (response.data as Blob).text()
      const json = JSON.parse(text)
      if (json.downloadUrl) {
        window.open(json.downloadUrl, '_blank')
        return
      }
    }

    // It's a file blob - trigger download
    const blob = response.data as Blob
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    // Try to get filename from content-disposition header
    const disposition = response.headers?.['content-disposition']
    let filename = defaultFileName
    if (disposition) {
      const filenameMatch = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '')
      }
    }
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  /**
   * Helper to check if import is still processing
   */
  isImportProcessing(status: ProductImportStatus | string): boolean {
    return status === ProductImportStatus.Pending ||
           status === ProductImportStatus.Processing ||
           status === 'Pending' ||
           status === 'Processing'
  },

  /**
   * Helper to check if import is complete (success or partial)
   */
  isImportComplete(status: ProductImportStatus | string): boolean {
    return status === ProductImportStatus.Completed ||
           status === ProductImportStatus.PartiallyCompleted ||
           status === ProductImportStatus.Failed ||
           status === 'Completed' ||
           status === 'PartiallyCompleted' ||
           status === 'Failed'
  },

  /**
   * Get color for import status badge
   */
  getImportStatusColor(status: ProductImportStatus | string): 'secondary' | 'info' | 'success' | 'warn' | 'danger' {
    const statusStr = typeof status === 'string' ? status : ProductImportStatus[status]
    switch (statusStr) {
      case 'Pending':
        return 'secondary'
      case 'Processing':
        return 'info'
      case 'Completed':
        return 'success'
      case 'PartiallyCompleted':
        return 'warn'
      case 'Failed':
        return 'danger'
      default:
        return 'secondary'
    }
  },

  // ============================================================================
  // Product Exports — async XLSX generation, results download from S3
  // ============================================================================

  /**
   * Queue a new products export. Returns immediately (202 Accepted); the
   * actual XLSX is generated by a background processor and downloadable
   * within ~30s for most catalogues. Rate-limited to 1 per 10 min per
   * user+shop on the backend — a 429 response means the user must wait.
   */
  async requestExport(
    shopId: string,
    filters?: {
      searchTerm?: string
      status?: number | string
      categoryId?: string
      baseCategoryId?: string
      productIds?: string[]
    }
  ): Promise<CreateProductExportResponse> {
    // Build a compact body — omit blanks so an all-null POST is byte-identical
    // to the pre-filter era and the backend's "export everything" path stays
    // the default. Send status as-is; the API accepts either the enum's int
    // value or its name.
    //
    // `productIds` (when the provider ticked row checkboxes) takes priority
    // server-side over the four filter fields — the resulting XLSX will
    // contain exactly those products. Still send the filters alongside so
    // the exports-history row can display "what was filtered" for context.
    const body: Record<string, unknown> = {}
    if (filters?.searchTerm && filters.searchTerm.trim().length > 0) {
      body.searchTerm = filters.searchTerm.trim()
    }
    if (filters?.status !== undefined && filters?.status !== null && filters.status !== '') {
      body.status = filters.status
    }
    if (filters?.categoryId) body.categoryId = filters.categoryId
    if (filters?.baseCategoryId) body.baseCategoryId = filters.baseCategoryId
    if (filters?.productIds && filters.productIds.length > 0) {
      body.productIds = filters.productIds
    }

    return await apiClient.post<CreateProductExportResponse>(
      `/shop/shops/${shopId}/exports`,
      body
    )
  },

  /** Paginated export history for a shop, newest first. */
  async getExports(shopId: string, page = 1, pageSize = 20): Promise<PaginatedResult<ProductExportDto>> {
    return await apiClient.get<PaginatedResult<ProductExportDto>>(
      `/shop/shops/${shopId}/exports`,
      { params: { page, pageSize } }
    )
  },

  /** Single export by ID — used for polling status of in-progress exports. */
  async getExport(exportId: string): Promise<ProductExportDto> {
    return await apiClient.get<ProductExportDto>(`/shop/exports/${exportId}`)
  },

  /**
   * Download a completed export. Backend returns either:
   *   • JSON `{ downloadUrl, fileName, expiresAt }` — S3 presigned URL (prod/staging) → window.open
   *   • Binary blob with XLSX content-type — local storage (dev) → trigger blob download
   * Mirrors the pattern used by downloadOriginalFile() for imports.
   */
  async downloadExport(exportId: string): Promise<void> {
    const response = await apiClient.request({
      method: 'GET',
      url: `/shop/exports/${exportId}/download`,
      responseType: 'blob'
    })

    const contentType = response.headers?.['content-type'] || ''
    if (contentType.includes('application/json')) {
      // Presigned URL response — open in new tab so browser handles the download.
      const text = await (response.data as Blob).text()
      const json = JSON.parse(text) as ProductExportDownloadResponse
      if (json.downloadUrl) {
        window.open(json.downloadUrl, '_blank')
        return
      }
    }

    // Binary blob — build an object URL and click an anchor to save it.
    const blob = response.data as Blob
    const url = window.URL.createObjectURL(blob)
    let filename = `products-export-${exportId}.xlsx`
    const disposition = response.headers?.['content-disposition']
    if (disposition) {
      const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) filename = match[1].replace(/['"]/g, '')
    }
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  /** Map enum/string to PrimeVue tag severity for status badges. */
  getExportStatusColor(status: ProductExportStatus | string): 'secondary' | 'info' | 'success' | 'danger' {
    const statusStr = typeof status === 'string' ? status : ProductExportStatus[status]
    switch (statusStr) {
      case 'Pending': return 'secondary'
      case 'Processing': return 'info'
      case 'Completed': return 'success'
      case 'Failed': return 'danger'
      default: return 'secondary'
    }
  },

  // ============================================================================
  // Helper Functions
  // ============================================================================

  getStatusLabel(status: ProductStatus): string {
    switch (status) {
      case 'Draft':
        return 'Draft'
      case 'Active':
        return 'Active'
      case 'Archived':
        return 'Archived'
      case 'OutOfStock':
        return 'Out of Stock'
      default:
        return 'Unknown'
    }
  },

  // ============================================================================
  // Product Options
  // ============================================================================

  async getOptionGroups(productId: string): Promise<ProductOptionGroup[]> {
    return await apiClient.get<ProductOptionGroup[]>(`/shop/products/${productId}/options`)
  },

  async createOptionGroup(productId: string, data: any): Promise<ProductOptionGroup> {
    return await apiClient.post<ProductOptionGroup>(`/shop/products/${productId}/options`, data)
  },

  async updateOptionGroup(productId: string, groupId: string, data: any): Promise<void> {
    await apiClient.put(`/shop/products/${productId}/options/${groupId}`, data)
  },

  async deleteOptionGroup(productId: string, groupId: string): Promise<void> {
    await apiClient.delete(`/shop/products/${productId}/options/${groupId}`)
  },

  async createOptionChoice(productId: string, groupId: string, data: any): Promise<any> {
    return await apiClient.post(`/shop/products/${productId}/options/${groupId}/choices`, data)
  },

  async updateOptionChoice(productId: string, groupId: string, choiceId: string, data: any): Promise<void> {
    await apiClient.put(`/shop/products/${productId}/options/${groupId}/choices/${choiceId}`, data)
  },

  async deleteOptionChoice(productId: string, groupId: string, choiceId: string): Promise<void> {
    await apiClient.delete(`/shop/products/${productId}/options/${groupId}/choices/${choiceId}`)
  },

  // ============================================================================
  // Utilities
  // ============================================================================

  getStatusColor(status: ProductStatus): 'secondary' | 'success' | 'warn' | 'danger' {
    switch (status) {
      case 'Draft':
        return 'secondary'
      case 'Active':
        return 'success'
      case 'Archived':
        return 'warn'
      case 'OutOfStock':
        return 'danger'
      default:
        return 'secondary'
    }
  },

  getUnitOfMeasureLabel(uom: UnitOfMeasure): string {
    switch (uom) {
      case 'Each':
        return 'Each'
      case 'Kilogram':
        return 'kg'
      case 'Gram':
        return 'g'
      case 'Litre':
        return 'L'
      case 'Millilitre':
        return 'ml'
      case 'Metre':
        return 'm'
      case 'Centimetre':
        return 'cm'
      case 'Pack':
        return 'Pack'
      case 'Box':
        return 'Box'
      case 'Dozen':
        return 'Dozen'
      default:
        return uom
    }
  }
}
